import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { MdLightMode, MdDarkMode, MdExitToApp, MdMenu, MdClose, MdFiberManualRecord } from 'react-icons/md';
import Dashboard from './pages/Dashboard';
import UserManagement from './pages/UserManagement';
import SMS from './pages/SMS';
import Broadcast from './pages/Broadcast';
import Templates from './pages/Templates';
import Reminders from './pages/Reminders';
import Schedule from './pages/Schedule';
import Log from './pages/Log';
import Login from './pages/Login';
import Footer from './components/Footer';
import './styles/App.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

function App() {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('darkMode');
    return saved ? JSON.parse(saved) : false;
  });
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const [smsUnreadCount, setSmsUnreadCount] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    document.body.classList.toggle('dark-mode', isDarkMode);
    localStorage.setItem('darkMode', JSON.stringify(isDarkMode));
  }, [isDarkMode]);

  useEffect(() => {
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!user) return;
    const roles = user.roles || [];
    const canReadSms = ['message_reader', 'message_sender', 'admin'].some((role) => roles.includes(role));
    if (!canReadSms) {
      setSmsUnreadCount(0);
      return;
    }

    const fetchSmsUnreadCount = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/messages/thread_summaries/`);
        setSmsUnreadCount(response.data.total_unread || 0);
      } catch (error) {
        console.error('Failed to load SMS unread count:', error);
      }
    };

    fetchSmsUnreadCount();
    const timerId = setInterval(fetchSmsUnreadCount, 30000);
    return () => clearInterval(timerId);
  }, [user]);

  const checkAuth = async () => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      navigate('/login');
      setLoading(false);
      return;
    }
    try {
      axios.defaults.headers.common['Authorization'] = `Token ${token}`;
      const response = await axios.get(`${API_BASE_URL}/users/me/`);
      setUser(response.data);
      setLoading(false);
    } catch (error) {
      localStorage.removeItem('authToken');
      navigate('/login');
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    navigate('/login');
  };

  const closeMenu = () => setMenuOpen(false);

  if (loading) return <div className="loading">Loading...</div>;
  if (!user) return (
    <>
      <Login setUser={setUser} />
      <Footer />
    </>
  );

  const roles = user.roles || [];
  const hasRole = (...r) => r.some(role => roles.includes(role));

  return (
    <div className="app-container">
      <header className="app-header">
        <div className="header-left">
          <button className="menu-toggle" onClick={() => setMenuOpen(!menuOpen)} title="Toggle menu">
            <span className="icon-with-indicator">
              {menuOpen ? <MdClose size={24} /> : <MdMenu size={24} />}
              {smsUnreadCount > 0 && <MdFiberManualRecord className="menu-toggle-unread-dot" size={10} />}
            </span>
          </button>
          <h1 className="app-title">Buertha Training</h1>
        </div>
        <div className="header-actions">
          <button className="theme-toggle" onClick={() => setIsDarkMode(!isDarkMode)} title="Toggle dark mode">
            {isDarkMode ? <MdLightMode size={24} /> : <MdDarkMode size={24} />}
          </button>
          <button className="logout-btn" onClick={handleLogout} title="Logout">
            <MdExitToApp size={24} />
          </button>
        </div>
      </header>

      <div className="app-content">
        <nav className={`tab-navigation ${menuOpen ? 'open' : ''}`}>
          <Link to="/" className="nav-link" onClick={closeMenu}>Dashboard</Link>
          <Link to="/users" className="nav-link" onClick={closeMenu}>Users</Link>
          {hasRole('message_reader', 'message_sender', 'admin') && (
            <Link to="/sms" className="nav-link nav-link-with-indicator" onClick={closeMenu}>
              <span>SMS</span>
              {smsUnreadCount > 0 && <MdFiberManualRecord className="nav-unread-icon" size={10} title="Unread messages" />}
            </Link>
          )}
          {hasRole('broadcast_sender', 'admin') && (
            <Link to="/broadcast" className="nav-link" onClick={closeMenu}>Broadcast</Link>
          )}
          {hasRole('template_editor', 'admin') && (
            <Link to="/templates" className="nav-link" onClick={closeMenu}>Templates</Link>
          )}
          {hasRole('template_editor', 'admin') && (
            <Link to="/reminders" className="nav-link" onClick={closeMenu}>Reminders</Link>
          )}
          {hasRole('schedule_editor', 'admin') && (
            <Link to="/schedule" className="nav-link" onClick={closeMenu}>Schedule</Link>
          )}
          {hasRole('admin') && (
            <Link to="/logs" className="nav-link" onClick={closeMenu}>Logs</Link>
          )}
        </nav>

        <main className="main-content">
          <Routes>
            <Route path="/" element={<Dashboard user={user} />} />
            <Route path="/users" element={<UserManagement user={user} setUser={setUser} />} />
            <Route path="/sms" element={<SMS user={user} onUnreadChange={setSmsUnreadCount} />} />
            <Route path="/broadcast" element={<Broadcast user={user} />} />
            <Route path="/templates" element={<Templates user={user} />} />
            <Route path="/reminders" element={<Reminders user={user} />} />
            <Route path="/schedule" element={<Schedule user={user} />} />
            <Route path="/logs" element={<Log user={user} />} />
          </Routes>
        </main>
      </div>
      <Footer />
    </div>
  );
}

export default App;
